import React, { useState, useEffect, useContext } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { mediaHook } from 'customHooks';

import useFacebookIntegration, {
  FacebookIntegrationProvider
} from 'contexts/loginToMeta.context';
import { ContextWrapper } from 'contexts/wrapper.context';
import { CONNECT_PLUGIN_MODAL } from 'constants/connect-plugin';
import { useModal } from 'contexts/modalProvider.context';
import {
  checkIsSafari,
  handleConnectionPlugin,
  platformLocalize,
  targetPlatform
} from 'components/connectPlugin/utils/helper';
import { getFilteredPlatforms } from 'utils/platform';

import SectionCard from 'components/connectPlugin/components/SectionCard/SectionCard';
import SelectBusinessAndPageModal from 'components/connectPlugin/components/SelectBusinessAndPageModal/SelectBusinessAndPageModal';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import BRModal from 'components/BRModal/BRModal';

import GlobeCover from 'assets/images/globeCover.png';

import './ConversationalCommerce.less';

const ConversationalCommerce = ({
  intl,
  mobileScreenSizes: { isSmallMobileScreen }
}) => {
  const { formatMessage } = intl;

  const { businessInfo } = useContext(ContextWrapper);
  const {
    isLoading,
    handleLoginToMeta,
    handleDisconnection,
    handleSyncProducts
  } = useContext(useFacebookIntegration);
  const { openModal } = useModal();

  const [LoggedPlatform, setLoggedPlatform] = useState('');
  const isConnected = (platform) =>
    businessInfo?.featuresConfigurations?.plugins?.omniMessaging &&
    businessInfo?.featuresConfigurations?.plugins?.omniMessaging?.[
      platform.keyName
    ]?.gaveAccess;
  const currentPlatform = (platform) =>
    CONNECT_PLUGIN_MODAL[platform?.keyName?.toUpperCase()];

  const renderSectionCardTitle = (platform) =>
    intl.formatMessage({
      id: isSmallMobileScreen ? platform?.nameResponsive : platform.name
    });

  const disconnectMessage = (platform) =>
    formatMessage(
      {
        id: 'chats_plugins.install_plugins.disconnected_successfully'
      },
      {
        platform: platformLocalize(platform)
      }
    );

  const openConnectionModal = (platform) => {
    const { isSafari, isIOS } = checkIsSafari();
    if (isSafari && isIOS) {
      openModal(BRModal, {
        coverImage: GlobeCover,
        modalClassName: 'br-safari-issue-modal',
        type: 'white-footer',
        width: '332px',
        contentTitle: (
          <span className="display-sm">
            {intl.formatMessage({
              id: 'chats_plugins.install_plugins.safari_issue'
            })}
          </span>
        ),
        contentBody: (
          <span className="body">
            {intl.formatMessage(
              {
                id: 'chats_plugins.install_plugins.safari_issue_subtitle'
              },
              {
                span: (children) => (
                  <span className="body-medium">{children}</span>
                )
              }
            )}
          </span>
        ),
        primaryLabel: intl.formatMessage({
          id: 'common.done'
        })
      });
    } else {
      handleConnectionPlugin(
        platform,
        businessInfo,
        LoggedPlatform,
        handleLoginToMeta,
        currentPlatform,
        handleSyncProducts,
        handleDisconnection,
        setLoggedPlatform,
        openModal
      );
    }
  };

  const handleManageConnection = (platform) => {
    localStorage.setItem('currentPlugin', JSON.stringify(platform));
    const connectedPlatform = targetPlatform(platform.keyName, businessInfo);
    openModal(SelectBusinessAndPageModal, {
      handleLoginToMeta,
      platform: connectedPlatform,
      isConnected,
      setLoggedPlatform,
      currentPlatform: currentPlatform(platform),
      cb: ({ metaBusinessId, pageId }) => {
        handleSyncProducts({
          metaBusinessId,
          pageId,
          platform: connectedPlatform?.keyName
        });
      },
      handleDisconnection: (platform) => {
        handleDisconnection({
          platform,
          msg: disconnectMessage(platform)
        });
      }
    });
  };

  return (
    <LoadingWrapper loading={isLoading}>
      <div className="br-chats-cards__container">
        <div className="br-chats-sales-channels__sections">
          {getFilteredPlatforms(businessInfo).map((platform, channelIndex) => (
            <SectionCard
              key={renderSectionCardTitle(platform)}
              name={renderSectionCardTitle(platform)}
              title={renderSectionCardTitle(platform)}
              subtitle={intl.formatMessage({
                id: `chats_plugins.install_plugins.${platform.description}`
              })}
              icon={platform.icon}
              isActivated={true}
              onButtonClicked={() =>
                isConnected(platform)
                  ? handleManageConnection(platform)
                  : openConnectionModal(platform)
              }
              btnTitle={
                isConnected(platform)
                  ? formatMessage({
                      id: 'chats_plugins.plugins_page.Manage'
                    })
                  : null
              }
            />
          ))}
        </div>
      </div>
    </LoadingWrapper>
  );
};

const ConversationalCommerceWithContext = (props) => {
  return (
    <FacebookIntegrationProvider>
      <ConversationalCommerce {...props} />
    </FacebookIntegrationProvider>
  );
};

export default mediaHook(
  injectIntl(withRouter(ConversationalCommerceWithContext))
);
